import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import Select from 'react-select';
import Button from '../../Button';
import useSchools from '../../../hooks/useSchools';
import GradeInput from '../../GradeInput';
import styles from './GetAccessCode.module.scss';
import { CustomerContext } from '../../../contexts/CustomerContext';
import BadStatus from './BadStatus';
import { Student, StudentImage } from '../../../models';
import NoSpringImagesWarning from '../../StudentIdForm/NoSpringImagesWarning';
import AffirmationText from '../../StudentIdForm/AffirmationText';
import routes from '../../../routes';

export default function GetAccessCode() {
  const schools = useSchools();
  const history = useHistory();

  const { customer, setCustomer } = useContext(CustomerContext);
  const defaultSchool = customer?.currentSchool ? { label: customer?.currentSchool.name, value: customer?.currentSchool.id } : {};

  const [email, setEmail] = useState(customer?.email || '');
  const [phone, setPhone] = useState('');
  const [school, setSchool] = useState(defaultSchool);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [grade, setGrade] = useState('');
  const [availableGrades, setAvailableGrades] = useState([]);
  const [studentId, setStudentId] = useState('');

  const [isDirty, setIsDirty] = useState(false);
  const [status, setStatus] = useState();
  const [accessCodes, setAccessCodes] = useState([]);
  const [student, setStudent] = useState();
  const [packageId, setPackageId] = useState();
  const [studentImageSeason, setStudentImageSeason] = useState('');
  const [springSeasonNotAvailable, setSpringSeasonNotAvailable] = useState(false);
  const [failedAttemptId, setFailedAttemptId] = useState();

  useEffect(() => {
    if (status !== 'OK' && accessCodes.length > 0) return;
    if (accessCodes.length === 0) return;

    const studentScope = Student.selectExtra(['availableSeasonsToPurchase']);

    StudentImage
      .selectExtra(['packageId'])
      .includes('student')
      .where({ forAccessCodeAndSchool: [accessCodes[0], school.value] })
      .merge({ student: studentScope })
      .all()
      .then((res) => {
        if (res.data.length === 0) return;

        setStudent(res.data[0].student);
        setCustomer({ ...customer, currentStudent: res.data[0].student, currentSchool: school })

        setPackageId(res.data[0].packageId);
        setStudentImageSeason(res.data[0].folder);
        setSpringSeasonNotAvailable(res.data[0].newSeasonNotAvailable);

        localStorage.setItem(`currentSeason-${res.data[0].student.id}`, res.data[0].folder);
      });
  }, [status, accessCodes]);

  useEffect(() => {
    if (school?.value) {
      axios.get(`/schools/${school.value}/grades`)
        .then((response) => {
          setAvailableGrades(response.data);
        })
        .catch((error) => {
          console.error('Failed to fetch available grades', error);
          setAvailableGrades([]);
        });
    } else {
      setAvailableGrades([]);
    }
  }, [school]);

  const changeHandler = (e, callback) => {
    setIsDirty(true);

    callback(e.target.value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    axios({
      method: 'post',
      url: '/students/search_for_student',
      baseURL: '/',
      data: {
        email,
        phone,
        grade,
        school_id: school.value,
        student_first_name: firstName,
        student_last_name: lastName,
        student_id: studentId,
      },
    })
      .then((res) => {
        if (typeof res.data === 'string') { // BAD status code
          setStatus(res.data);
        } else {
          setStatus('OK');
          setAccessCodes(res.data);
        }

        window.scrollTo(0, 0);
      })
      .catch((err) => {
        if (err.response.data.id) setFailedAttemptId(err.response.data.id);
        setStatus('BAD');
      })
      .finally(() => {
        setIsDirty(false);
        window.scrollTo(0, 0);
      });
  };

  if (status && status === 'BAD') {
    return <BadStatus schoolId={school.value} email={email} failedAttemptId={failedAttemptId} />;
  }

  if (student) {
    return (
      <div className={styles.continueContainer}>
        {springSeasonNotAvailable && <NoSpringImagesWarning season={studentImageSeason} />}
        <AffirmationText
          handleContinue={() => {
            localStorage.setItem('isUsingAccessCode', 'true');
            history.push(routes.packageSelection({ pid: packageId }));
          }}
          student={student}
        />
      </div>
    );
  }

  return (
    <div className="container">
      <h1 className={styles.header}>Request Access Code</h1>
      {status && status === 'BAD' && (
        <p className={styles.noAccessCode}>
          We weren&apos;t able to find a student with your provided contact information.
          <br />
          We will try to find your student via the information you provided and
          get back to you with an access code shortly.
        </p>
      )}
      <div className={styles.getAccessCodePage}>
        <form onSubmit={handleSubmit}>
          <hr />
          <div className="row">
            <div className="col-sm-6">
              <label>
                Email
                <input required={!phone} type="email" value={email} onChange={(e) => changeHandler(e, setEmail)} />
              </label>
            </div>
            <div className="col-sm-6">
              <label>
                Phone Number
                <input required={!email} type="phone" value={phone} onChange={(e) => changeHandler(e, setPhone)} />
              </label>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-12">
              <label htmlFor="school-input">
                School Name
                <Select
                  required={studentId}
                  id="school-input"
                  defaultValue={school}
                  options={schools.map((selectSchool) => (
                    ({ value: selectSchool.id, label: selectSchool.name })
                  ))}
                  onChange={(opt) => {
                    setIsDirty(true);
                    setSchool(opt);
                  }}
                />
              </label>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-6">
              <label>
                <div>
                  <strong>STUDENT&apos;S</strong>
                  {' '}
                  First Name
                </div>
                <input required={!studentId} value={firstName} onChange={(e) => changeHandler(e, setFirstName)} />
              </label>
            </div>
            <div className="col-sm-6">
              <label>
                <div>
                  <strong>STUDENT&apos;S</strong>
                  {' '}
                  Last Name
                </div>
                <input required={!studentId} value={lastName} onChange={(e) => changeHandler(e, setLastName)} />
              </label>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-6">
              <label>
                Grade
                <GradeInput
                  val={grade}
                  onChange={(newVal) => {
                    setIsDirty(true);
                    setGrade(newVal);
                  }}
                  availableGrades={availableGrades}
                />
              </label>
            </div>
            <div className="col-sm-6">
              <label style={{ marginBottom: '1em', display: 'block' }}>
                <div>
                  Student ID (optional)
                </div>
                <input
                  style={{ width: '100%' }}
                  value={studentId}
                  onChange={(e) => changeHandler(e, setStudentId)}
                />
              </label>
            </div>
          </div>
          <Button disabled={!isDirty} variant="primary" fullWidth type="submit">
            Search
          </Button>
        </form>
      </div>
    </div>
  );
}
